// Day js
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
// Translation
import i18n from "i18next";
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next, useTranslation } from "react-i18next";
// React
import { useEffect } from "react";
// React router dom
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { toPublicUrl } from "./services/PublicUrl";
// Pages
import Home from "./pages/Home/Home";
import InProgress from './pages/InProgress/InProgress';
import Error from './pages/Error/Error';
import Organizations from "./pages/Organizations/Organizations";
import OrganizationDetails from "./pages/OrganizationDetails/OrganizationDetails";
import OrganizationStructure from "./pages/OrganizationStructure/OrganizationStructure";
import LocationStructure from "./pages/LocationStructure/LocationStructure";
import HealthcareServiceStructure from "./pages/HealthcareServiceStructure/HealthcareServiceStructure";
import HealthcareServices from "./pages/HealthcareServices/HealthcareServices";
import HealthcareServiceDetails from "./pages/HealthcareServiceDetails/HealthcareServiceDetails";
import Locations from "./pages/Locations/Locations";
import LocationDetails from "./pages/LocationDetails/LocationDetails";
import Individuals from "./pages/Individuals/Individuals";
import IndividualDetails from "./pages/IndividualDetails/IndividualDetails";
import ImplementationGuidePage from './pages/ImplementationGuide/ImplementationGuidePage';

require('dayjs/locale/fr');

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['fr', 'en'],
    backend: {
      loadPath: toPublicUrl("/locales/{{lng}}/{{ns}}.json"),
    },
  })

dayjs.extend(relativeTime);
dayjs.locale(i18n.language);

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;

  useTranslation();

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    let title = "";
    let metaDescription = "";

    switch (pathname) {
      case "/":
      case "/Juno":
      case "/Home":
      case "/Juno/Home":
        title = "Home page";
        metaDescription = "";
        break;
      case "/Error":
        title = "Oops !";
        metaDescription = "";
        break;
      case "/InProgress":
        title = "Coming Soon";
        metaDescription = "";
        break;
      case "/Users":
      case "/Individuals":
        title = "Individuals";
        metaDescription = "";
        break;
      case "/Organizations":
        title = "Organizations";
        metaDescription = "";
        break;
      case "/HealthcareServices":
        title = "Healthcare Services";
        metaDescription = "";
        break;
      case "/Locations":
        title = "Locations";
        metaDescription = "";
        break;
      case "/OrganizationStructure":
        title = "Organization Structure";
        metaDescription = "";
        break;
      case "/LocationStructure":
        title = "Location Structure";
        metaDescription = "";
        break;
      case "/HealthcareServiceStructure":
        title = "Healthcare Service Structure";
        metaDescription = "";
        break;
      case "/User":
        title = "Individual Details";
        metaDescription = "";
        break;
      case "/Organization":
        title = "Organization Details";
        metaDescription = "";
        break;
      case "/HealthcareService":
        title = "Healthcare Service Details";
        metaDescription = "";
        break;
      case "/Location":
        title = "Location Details";
        metaDescription = "";
        break;
      case "/ImplementationGuide":
        title = "FHIR Implementation Guide";
        metaDescription = "";
        break;
    }

    if (pathname.startsWith("/Individuals/")) {
      title = "Individuals";
    }

    if (pathname.startsWith("/User/")) {
      title = "Individual Details";
    }

    if (pathname.startsWith("/Organization/")) {
      title = "Organization Details";
    }

    if (pathname.startsWith("/LocationStructure")) {
      title = "Location Structure";
    }

    if (pathname.startsWith("/HealthcareServiceStructure")) {
      title = "Healthcare Service Structure";
    }

    if (pathname.startsWith("/HealthcareService/")) {
      title = "Healthcare Service Details";
    }

    if (pathname.startsWith("/Location/")) {
      title = "Location Details";
    }

    if (title) {
      document.title = title;
    }

    if (metaDescription) {
      const metaDescriptionTag: HTMLMetaElement | null = document.querySelector(
        'head > meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription;
      }
    }
  }, [pathname]);

  return (
    <Routes>
      <Route index element={<Home />} />
      <Route
        path="/"
        element={<Home />}
      />
      <Route
        path="/Home"
        element={<Home />}
      />
      <Route
        path="/Juno"
        element={<Home />}
      />
      <Route
        path="/Juno/Home"
        element={<Home />}
      />
      <Route
        path="/Users"
        element={<Navigate replace to="/Individuals/users" />}
      />
      <Route
        path="/Individuals"
        element={<Navigate replace to="/Individuals/users" />}
      />
      <Route
        path="/Individuals/:section"
        element={<Individuals />}
      />
      <Route
        path="/Organizations"
        element={<Organizations />}
      />
      <Route
        path="/HealthcareServices"
        element={<HealthcareServices />}
      />
      <Route
        path="/Locations"
        element={<Locations />}
      />
      <Route
        path="/User/:userId"
        element={<IndividualDetails forcedResourceType="Person" />}
      />
      <Route
        path="/Individuals/:resourceType/:resourceId"
        element={<IndividualDetails />}
      />
      <Route
        path="/Organization/:organizationId"
        element={<OrganizationDetails />}
      />
      <Route
        path="/HealthcareService/:healthcareServiceId"
        element={<HealthcareServiceDetails />}
      />
      <Route
        path="/Location/:locationId"
        element={<LocationDetails />}
      />
      <Route
        path="/OrganizationStructure"
        element={<OrganizationStructure />}
      />
      <Route
        path="/LocationStructure"
        element={<LocationStructure />}
      />
      <Route
        path="/HealthcareServiceStructure"
        element={<HealthcareServiceStructure />}
      />
      <Route
        path="/InProgress"
        element={<InProgress />}
      />
      <Route
        path="/Error"
        element={<Error />}
      />
      <Route 
        path="/ImplementationGuide" 
        element={<ImplementationGuidePage />} 
      />
      <Route path="*" element={<Error notFound />} />
    </Routes>
  );
}

export default App;
